import { Box, useMediaQuery } from '@mui/material'
import { BAR_CHART_DOMAIN_RANGES, BAR_CHART_TICK_RANGES, COLOR } from 'constant'
import { useAppSelector } from 'hooks'
import React, { useCallback, useMemo, useState } from 'react'
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, LineChart, Line, Legend } from 'recharts'
import { LineChartProps } from 'types'
import { formatNumberChart } from 'utils/formatNumber'
import { getCountryNameByShortCode } from 'utils/getCountryList'

const CustomSquareDot = ({ cx, cy, stroke, fill }: any) => {
  const size = 8 // Size of the square
  return (
    <rect
      x={cx - size / 2}
      y={cy - size / 2}
      width={size}
      height={size}
      fill={stroke === '#fff' ? fill : stroke}
      stroke='none'
    />
  )
}

const CustomLineDot = ({ cx, cy, stroke, fill, payload, dataKey }: any) => {
  // Only render dot if the value for this country is not null
  if (payload && payload[dataKey] !== null && payload[dataKey] !== undefined && !isNaN(payload[dataKey])) {
    const size = 8;
    return (
      <rect
        x={cx - size / 2}
        y={cy - size / 2}
        width={size}
        height={size}
        fill={stroke === '#fff' ? fill : stroke}
        stroke='none'
      />
    );
  }
  return null;
};

const LineChartOT = ({
  data,
  height,
  axisFontSize = 14,
  gridBackground = '#fff',
  gridColor = '#f5f5f5',
  isTickLine = false,
  tickColor = '#000',
  tickCount = 6,
  showPercentage = false,
  selectedCountries,
}: LineChartProps) => {
  // Filter countries that have valid data
  const validCountries = useMemo(() => {
    return selectedCountries.filter(country => {
      // Check if this country has any non-null, non-undefined values across all years
      return data.some(yearData => {
        const value = (yearData as any)[country];
        return value !== null && value !== undefined && !isNaN(value);
      });
    });
  }, [selectedCountries, data]);

  const dataValArr = validCountries.flatMap((i: any) => {
    return data.map((j: any) => j[i]).filter(val => val !== null && val !== undefined && !isNaN(val))
  })
  const isSmaller600 = useMediaQuery('(max-width:600px)')
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)

  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null)

  const calcDomain = useMemo(() => {
    if (!data?.length || dataValArr.length === 0) return undefined
    const maxValue = Math.max(...dataValArr)
    for (const range of BAR_CHART_DOMAIN_RANGES) {
      if (maxValue <= range) {
        return [0, range]
      }
    }
    return undefined
  }, [data, dataValArr])

  const calcTicks = useMemo(() => {
    if (!data?.length || dataValArr.length === 0) return undefined
    const maxValue = Math.max(...dataValArr)
    for (const range of BAR_CHART_TICK_RANGES(false)) {
      if (maxValue <= range.max) {
        return range.ticks
      }
    }
    return undefined
  }, [data, dataValArr])

  const CustomTooltip = useCallback(
    (props: any) => {
      const { payload } = props
      return (
        <Box sx={{ background: '#fff', padding: '6px 12px', border: `1px solid ${COLOR.DARK}`, borderRadius: '12px' }}>
          <ul>
            {payload.map((entry: any) => (
              <li key={entry.color} style={{ color: entry.color }}>
                <span>{entry.name} : </span>
                <span>
                  {showPercentage ? `${entry.value.toFixed(2)}%` : `${formatNumberChart(entry.value, 2, true)}`}
                </span>
              </li>
            ))}
          </ul>
        </Box>
      )
    },
    [showPercentage],
  )

  const CustomLegend = useCallback((props: any) => {
    const { payload } = props
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', marginBottom: '20px' }}>
        {payload.map((entry: any, index: number) => (
          <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div
              style={{
                width: '14px',
                height: '14px',
                backgroundColor: entry.color,
              }}
            />
            <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
              {getCountryNameByShortCode(entry.value, countryData)}
            </span>
          </div>
        ))}
      </div>
    )
  }, [])

  const getLineColor = useCallback((idx: number) => {
    switch (idx) {
      case 0:
        return COLOR.BAR1
      case 1:
        return COLOR.BAR2
      case 2:
        return COLOR.BAR3
      case 3:
        return COLOR.BAR4
      case 4:
        return COLOR.BAR5

      default:
        return COLOR.BAR6
    }
  }, [])

  const getActiveLineColor = useCallback((idx: number) => {
    switch (idx) {
      case 0:
        return COLOR.BAR1LIGHT
      case 1:
        return COLOR.BAR2LIGHT
      case 2:
        return COLOR.BAR3LIGHT
      case 3:
        return COLOR.BAR4LIGHT
      case 4:
        return COLOR.BAR5LIGHT

      default:
        return COLOR.BAR6LIGHT
    }
  }, [])

  // If no selected countries, return empty state or null
  if (selectedCountries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <span>No countries selected</span>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', left: { xs: '-18px', sm: '-6px' }, overflow: { xs: 'scroll', sm: 'hidden' } }}>
      <ResponsiveContainer width={'100%'} height={height} minWidth={'500px'}>
        <LineChart
          data={data} //isSmaller600 ? reversedData : data
          // layout={isSmaller600 ? 'vertical' : 'horizontal'}
          margin={{
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <Legend
            layout='horizontal' // Stacks legends vertically
            verticalAlign='top' // Positions at the top
            align='left' // Aligns to the left
            content={<CustomLegend />}
          />
          {<CartesianGrid strokeDasharray='0 0' stroke={gridColor} fill={gridBackground} />}
          <XAxis
            // type={isSmaller600 ? 'number' : 'category'}
            // dataKey={isSmaller600 ? 'value' : 'year'}
            // scale={isSmaller600 ? 'auto' : 'point'}
            padding={{
              left: isSmaller600 ? 30 * 1.22 : 70 * 1.22,
              right: isSmaller600 ? 30 * 1.2 : 70 * 1.2,
            }}
            dataKey={'year'}
            scale={'point'}
            tickFormatter={
              (value) => value
              // !isSmaller600
              //   ? value
              //   : showPercentage
              //     ? `${value}%`
              //     : value < 1
              //       ? `${value}`
              //       : formatNumberChart(value, 2)
            }
            tickLine={isTickLine}
            tick={{ fontSize: axisFontSize, fontWeight: 500, fill: tickColor }}
            tickCount={tickCount}
            ticks={undefined}
            domain={undefined}
          />
          <YAxis
            // type={isSmaller600 ? 'category' : 'number'}
            // dataKey={isSmaller600 ? 'year' : 'value'}
            // scale={isSmaller600 ? 'point' : 'auto'}
            type={'number'}
            scale={'auto'}
            tickFormatter={(value) =>
              showPercentage ? `${value}%` : value < 1 ? `${value}` : (formatNumberChart(value, 2) as string)
            }
            tickLine={isTickLine}
            tick={{ fontSize: axisFontSize, fontWeight: 500, fill: tickColor }}
            tickCount={undefined}
            ticks={calcTicks}
            domain={calcDomain}
          />
          <Tooltip content={<CustomTooltip />} />
          {selectedCountries.map((eachCountry, index) => {
            const hasValidData = validCountries.includes(eachCountry);
            return (
              <Line
                key={eachCountry}
                legendType='square'
                type='linear'
                dataKey={eachCountry}
                onMouseEnter={() => {
                  setHoveredBarIndex(index)
                }}
                onMouseLeave={() => {
                  setHoveredBarIndex(null)
                }}
                stroke={hasValidData ? (index !== hoveredBarIndex ? getLineColor(index) : getActiveLineColor(index)) : '#cccccc'}
                strokeWidth={hasValidData ? 2 : 0}
                isAnimationActive={false}
                activeDot={
                  hasValidData ? (props: any) => {
                    // Only show active dot if the value is not null
                    if (props.payload && props.payload[eachCountry] !== null && props.payload[eachCountry] !== undefined && !isNaN(props.payload[eachCountry])) {
                      return <CustomSquareDot {...props} fill={index !== hoveredBarIndex ? getLineColor(index) : getActiveLineColor(index)} />
                    }
                    return <g></g>; // Return empty SVG element instead of null
                  } : false
                }
                dot={hasValidData ? (props: any) => (
                  <CustomLineDot {...props} dataKey={eachCountry} fill={index !== hoveredBarIndex ? getLineColor(index) : getActiveLineColor(index)} />
                ) : false}
                connectNulls={true}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default LineChartOT
